import ToggleButtonGroup from "@material-ui/lab/ToggleButtonGroup";
import ToggleButton from "@material-ui/lab/ToggleButton";
import assets from "../../assets";
import {
  Divider,
  makeStyles,
  Paper,
  Theme,
  ThemeProvider,
  Tooltip,
  withStyles,
} from "@material-ui/core";
import { Translate } from "react-admin";
import { useState } from "react";
import BluetoothAudioIcon from "@material-ui/icons/BluetoothAudio";
import BluetoothDisabledIcon from "@material-ui/icons/BluetoothDisabled";
import classNames from "classnames";

export const dutyStatusAbbreviations = {
  ON: ["ON", "YM"],
  OFF: ["OFF", "PC"],
  D: ["D"],
  SB: ["SB"],
};

export type SelectedDutyStatusGroup =
  | keyof typeof dutyStatusAbbreviations
  | "all";

export type SelectedOnlineStatusGroup = "all" | "online" | "offline";

export type SelectedGroup = SelectedDutyStatusGroup | SelectedOnlineStatusGroup;

export type SelectedGroupChangedEventArgs = {
  dutyStatus: SelectedDutyStatusGroup;
  onlineStatus: SelectedOnlineStatusGroup;
};

interface MapSummaryProps {
  theme: Theme;
  onDuty: number;
  offDuty: number;
  driving: number;
  sleeper: number;
  connected: number;
  disconnected: number;
  all: number;
  translate: Translate;
  onGroupChanged: (group: SelectedGroupChangedEventArgs) => void;
  initialGroups?: SelectedGroupChangedEventArgs;
}

const getStatusColor = (status: string, theme: Theme) => {
  switch (status) {
    case "OFF":
      return theme.palette.grey[900];
    case "ON":
      return theme.palette.info.main;
    case "D":
      return theme.palette.success.main;
    case "SB":
      return theme.palette.warning.main;
    default:
      return "auto";
  }
};

const useStyles = makeStyles((theme) => ({
  paper: {
    display: "flex",
    border: `1px solid ${theme.palette.divider}`,
    flexWrap: "wrap",
  },
  divider: {
    margin: theme.spacing(1, 0.5),
  },
}));

const StyledToggleButtonGroup = withStyles((theme) => ({
  grouped: {
    margin: theme.spacing(0.5),
    border: "none",
    "&:not(:first-child)": {
      borderRadius: theme.shape.borderRadius,
    },
    "&:first-child": {
      borderRadius: theme.shape.borderRadius,
    },
  },
}))(ToggleButtonGroup);

export const MapSummary = ({
  theme,
  all,
  onDuty,
  offDuty,
  driving,
  sleeper,
  disconnected,
  connected,
  translate,
  onGroupChanged,
  initialGroups = { dutyStatus: "all", onlineStatus: "all" },
}: MapSummaryProps) => {
  const classes = useStyles();
  const [groupByDutyStatus, setGroupByDutyStatus] = useState<
    SelectedDutyStatusGroup
  >(initialGroups.dutyStatus);
  const [groupByOnlineStatus, setGroupByOnlineStatus] = useState<
    SelectedOnlineStatusGroup
  >(initialGroups.onlineStatus);

  const handleDutyStatusGroupChange = (
    _: React.MouseEvent<HTMLElement, MouseEvent>,
    value: any
  ) => {
    setGroupByDutyStatus(value?.length ? value : groupByDutyStatus);
    onGroupChanged({
      onlineStatus: groupByOnlineStatus,
      dutyStatus: value?.length ? value : groupByDutyStatus,
    });
  };

  const handleOnlineStatusGroupChange = (
    _: React.MouseEvent<HTMLElement, MouseEvent>,
    value: any
  ) => {
    setGroupByOnlineStatus(value?.length ? value : "all");
    onGroupChanged({
      onlineStatus: value?.length ? value : "all",
      dutyStatus: groupByDutyStatus,
    });
  };

  return (
    <ThemeProvider theme={theme}>
      <Paper
        className={classNames("current-locations-map-summary", classes.paper)}
        style={{ backgroundColor: theme.palette.background.paper }}
      >
        <StyledToggleButtonGroup
          size="small"
          exclusive
          value={groupByDutyStatus}
          onChange={handleDutyStatusGroupChange}
          orientation="horizontal"
        >
          <ToggleButton value="all">
            <div>
              <Tooltip title="All">
                <div className="duty-status">
                  <assets.icon />
                </div>
              </Tooltip>
              <div>{all}</div>
            </div>
          </ToggleButton>
          <ToggleButton value="ON">
            <div>
              <Tooltip title={translate("appData.dutyCycles.ON")}>
                <div
                  className="duty-status"
                  style={{
                    backgroundColor: getStatusColor("ON", theme),
                    color: theme.palette.getContrastText(
                      getStatusColor("ON", theme)
                    ),
                  }}
                >
                  ON
                </div>
              </Tooltip>
              <div>{onDuty}</div>
            </div>
          </ToggleButton>
          <ToggleButton value="OFF">
            <div>
              <Tooltip title={translate("appData.dutyCycles.OFF")}>
                <div
                  className="duty-status"
                  style={{
                    backgroundColor: getStatusColor("OFF", theme),
                    color: theme.palette.getContrastText(
                      getStatusColor("OFF", theme)
                    ),
                  }}
                >
                  OF
                </div>
              </Tooltip>
              <div>{offDuty}</div>
            </div>
          </ToggleButton>
          <ToggleButton value="D">
            <div>
              <Tooltip title={translate("appData.dutyCycles.D")}>
                <div
                  className="duty-status"
                  style={{
                    backgroundColor: getStatusColor("D", theme),
                    color: theme.palette.getContrastText(
                      getStatusColor("D", theme)
                    ),
                  }}
                >
                  DR
                </div>
              </Tooltip>
              <div>{driving}</div>
            </div>
          </ToggleButton>
          <ToggleButton value="SB">
            <div>
              <Tooltip title={translate("appData.dutyCycles.SB")}>
                <div
                  className="duty-status"
                  style={{
                    backgroundColor: getStatusColor("SB", theme),
                    color: theme.palette.getContrastText(
                      getStatusColor("SB", theme)
                    ),
                  }}
                >
                  SB
                </div>
              </Tooltip>
              <div>{sleeper}</div>
            </div>
          </ToggleButton>
        </StyledToggleButtonGroup>
        <Divider flexItem orientation="vertical" className={classes.divider} />
        <StyledToggleButtonGroup
          size="small"
          exclusive
          value={groupByOnlineStatus}
          onChange={handleOnlineStatusGroupChange}
          orientation="horizontal"
        >
          <ToggleButton value="online">
            <div>
              <Tooltip
                title={translate(
                  "resources.locations-current.labels.connected"
                )}
              >
                <div className="duty-status">
                  <BluetoothAudioIcon
                    fontSize="small"
                    style={{ color: theme.palette.success.main }}
                  />
                </div>
              </Tooltip>
              <div>{connected}</div>
            </div>
          </ToggleButton>
          <ToggleButton value="offline">
            <div>
              <Tooltip
                title={translate(
                  "resources.locations-current.labels.disconnected"
                )}
              >
                <div className="duty-status">
                  <BluetoothDisabledIcon
                    fontSize="small"
                    style={{ color: theme.palette.error.main }}
                  />
                </div>
              </Tooltip>
              <div>{disconnected}</div>
            </div>
          </ToggleButton>
        </StyledToggleButtonGroup>
      </Paper>
    </ThemeProvider>
  );
};
